import { TrendingUp, Users, Navigation, ShoppingBag, BarChart3, Share2 } from "lucide-react"
import type { ReactNode } from "react"

import {
  CHECKLIST_LABELS,
  ChecklistItem,
  ChecklistProgressBar,
  checklistProgress,
  type ChecklistLinks,
} from "@/components/shared/checklist"
import { AdminLayout } from "@/layouts/AdminLayout"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { PropertyDashboardChecklist, PropertySummary, TopPOIItem } from "@/lib/types"

interface Props {
  property: PropertySummary
  guest_sessions_7d: number
  guest_sessions_30d: number
  top_pois: TopPOIItem[]
  directions_clicks: number
  booking_clicks: number
  active_shares_count: number
  checklist: PropertyDashboardChecklist
  urls: {
    settings_branding: string
    settings_wifi: string
    inside: string
    guides: string
    delivery: string
    pois: string
  }
}

function dashboardChecklistLinks(urls: Props["urls"]): ChecklistLinks {
  return {
    logo_added:            urls.settings_branding,
    wifi_configured:       urls.settings_wifi,
    inside_info_published: urls.inside,
    default_guide_active:  urls.guides,
    qr_generated:          urls.delivery,
    pois_count_ok:         urls.pois,
    first_guest_session:   null,
  }
}

function KpiCard({ label, value, icon: Icon }: { label: string; value: number; icon: React.ElementType }) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="size-3.5" aria-hidden="true" />
        {label}
      </div>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}

export default function PropertyDashboard({
  property,
  guest_sessions_7d,
  guest_sessions_30d,
  top_pois,
  directions_clicks,
  booking_clicks,
  active_shares_count,
  checklist,
  urls,
}: Props) {

  const { keys: checklistKeys, completedCount, totalCount, allComplete, progressPct } =
    checklistProgress(checklist)
  const checklistLinks = dashboardChecklistLinks(urls)

  const allKpisZero =
    guest_sessions_7d === 0 &&
    guest_sessions_30d === 0 &&
    directions_clicks === 0 &&
    booking_clicks === 0 &&
    top_pois.length === 0

  const showEmptyState = allKpisZero && !allComplete

  return (
    <div className="mx-auto max-w-5xl flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Left column — KPIs (2/3 width) */}
        <div className="flex flex-col gap-6 md:col-span-2">
          {showEmptyState ? (
            /* Empty state: no activity yet — replace KPI section with a prompt */
            <div className="rounded-lg border border-dashed py-12 text-center">
              <p className="text-base font-medium">No guest activity yet.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Sessions, clicks, and top POIs will appear here once guests start arriving.
              </p>
            </div>
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 gap-3">
                <KpiCard label="Guest sessions (7d)"    value={guest_sessions_7d}    icon={Users}       />
                <KpiCard label="Guest sessions (30d)"   value={guest_sessions_30d}   icon={TrendingUp}  />
                <KpiCard label="Directions clicks (30d)" value={directions_clicks}   icon={Navigation}  />
                <KpiCard label="Booking clicks (30d)"   value={booking_clicks}       icon={ShoppingBag} />
              </div>

              {/* Active shares — 5th KPI in its own row so it doesn't orphan in a 2-col grid */}
              <KpiCard label="Active guide shares" value={active_shares_count} icon={Share2} />

              {/* Top POIs */}
              {top_pois.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                      <BarChart3 className="size-4" aria-hidden="true" />
                      Top 5 POIs by views (30d)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ol className="flex flex-col gap-2">
                      {top_pois.map((poi, i) => (
                        <li key={poi.poi_id ?? i} className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-sm">
                            <span className="w-4 text-xs tabular-nums text-muted-foreground">
                              {i + 1}.
                            </span>
                            {poi.name}
                          </span>
                          <span className="text-sm tabular-nums text-muted-foreground">
                            {poi.count} views
                          </span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Right column — Checklist (1/3 width) */}
        <div className="md:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Setup checklist</CardTitle>
              {allComplete ? (
                <Badge variant="default" className="w-fit bg-green-500 hover:bg-green-500">
                  All set!
                </Badge>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{completedCount} of {totalCount} complete</span>
                    <span>{progressPct}%</span>
                  </div>
                  <ChecklistProgressBar progressPct={progressPct} />
                </div>
              )}
            </CardHeader>

            {!allComplete && (
              <CardContent className="pt-0">
                <div className="flex flex-col divide-y divide-border">
                  {checklistKeys.map((key) => (
                    <ChecklistItem
                      key={key}
                      label={CHECKLIST_LABELS[key]}
                      done={checklist[key]}
                      href={checklistLinks[key]}
                    />
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

PropertyDashboard.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
